import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mic, Square, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Mic button for one-shot dictation.
 *
 * - Tap once to start recording (mic icon).
 * - Tap again to stop; audio is uploaded to /api/admin/transcribe (Whisper).
 * - On success we call onTranscript(text) — the parent decides whether to
 *   append, replace, or send.
 *
 * We intentionally use tap-to-start / tap-to-stop rather than press-and-hold
 * so it works one-handed on a phone in a pocket.
 */
export const VoiceDictateButton = ({
  onTranscript,
  label = "Dictate",
  size = "md",  // "sm" | "md" | "lg"
  className = "",
  disabled = false,
  hint = null,
  testId = "voice-dictate",
}) => {
  const [state, setState] = useState("idle"); // idle | recording | transcribing
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const [seconds, setSeconds] = useState(0);

  useEffect(() => () => stop(true), []); // cleanup on unmount

  const start = async () => {
    if (state !== "idle" || disabled) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : "audio/webm";
      const mr = new MediaRecorder(stream, { mimeType: mime });
      mediaRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = handleStop;
      mr.start(250);
      setState("recording");
      setSeconds(0);
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch (e) {
      toast.error("Microphone access denied. Enable it in Settings.");
    }
  };

  const stop = (silent = false) => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    const mr = mediaRef.current;
    if (!mr) return;
    try {
      if (mr.state !== "inactive") mr.stop();
      mr.stream.getTracks().forEach((t) => t.stop());
    } catch { /* ignore */ }
    mediaRef.current = null;
    if (silent) setState("idle");
  };

  const handleStop = async () => {
    setState("transcribing");
    try {
      const blob = new Blob(chunksRef.current, { type: "audio/webm" });
      if (blob.size < 800) {
        toast.info("Nothing heard — try again.");
        setState("idle");
        return;
      }
      const fd = new FormData();
      fd.append("file", blob, `dictation-${Date.now()}.webm`);
      const { data } = await axios.post(`${API}/admin/transcribe`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data.transcript) {
        onTranscript(data.transcript);
      } else {
        toast.error("Couldn't hear that clearly — try again in a quieter spot.");
      }
    } catch (e) {
      toast.error("Transcription failed.");
    } finally {
      setState("idle");
    }
  };

  const dims = size === "sm" ? "w-9 h-9" : size === "lg" ? "w-14 h-14" : "w-11 h-11";
  const iconSize = size === "sm" ? 14 : size === "lg" ? 20 : 16;

  const recording = state === "recording";
  const busy = state === "transcribing";

  return (
    <div className={`inline-flex items-center gap-2 ${className}`}>
      <button
        type="button"
        onClick={recording ? () => { const mr = mediaRef.current; if (mr) mr.stop(); if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; } } : start}
        disabled={disabled || busy}
        aria-label={recording ? "Stop recording" : label}
        data-testid={testId}
        className={`${dims} inline-flex items-center justify-center rounded-full border transition ${
          recording
            ? "bg-[#B76E79] border-[#B76E79] text-white animate-pulse shadow-md"
            : busy
              ? "bg-[#F5EFE6] border-[#DDD1BE] text-[#6A5F58]"
              : "bg-white border-[#DDD1BE] text-[#6A5F58] hover:border-[#B76E79] hover:text-[#B76E79]"
        } disabled:opacity-50`}
      >
        {busy ? <Loader2 size={iconSize} className="animate-spin" /> : recording ? <Square size={iconSize} fill="currentColor" /> : <Mic size={iconSize} />}
      </button>
      {recording && (
        <span className="text-xs font-brand tracking-[0.14em] uppercase text-[#B76E79]" data-testid={`${testId}-timer`}>
          Recording · {String(Math.floor(seconds / 60)).padStart(1, "0")}:{String(seconds % 60).padStart(2, "0")}
        </span>
      )}
      {busy && (
        <span className="text-xs font-brand tracking-[0.14em] uppercase text-[#6A5F58]">
          Transcribing…
        </span>
      )}
      {!recording && !busy && hint && (
        <span className="text-xs text-[#6A5F58]">{hint}</span>
      )}
    </div>
  );
};

/* -------------------------------------------------------------------------
 * TTS helper — read a string aloud with the browser's Web Speech API.
 * Used by the chat panel when conversation mode is on.
 * ------------------------------------------------------------------------- */
export const speak = (text, { onEnd } = {}) => {
  try {
    if (!("speechSynthesis" in window)) return;
    window.speechSynthesis.cancel(); // stop any previous utterance
    const u = new SpeechSynthesisUtterance(text);
    // Prefer a British English voice if available
    const voices = window.speechSynthesis.getVoices();
    const uk = voices.find((v) => /en-GB/i.test(v.lang));
    if (uk) u.voice = uk;
    u.rate = 1.0; u.pitch = 1.0; u.volume = 1.0;
    if (onEnd) u.onend = onEnd;
    window.speechSynthesis.speak(u);
  } catch { /* ignore */ }
};

export const stopSpeaking = () => {
  try { if ("speechSynthesis" in window) window.speechSynthesis.cancel(); } catch { /* ignore */ }
};
